import { apFirst as apFirst_, apS as apS_, apSecond as apSecond_ } from 'fp-ts/Apply';
import * as E from 'fp-ts/Either';
import { eqStrict } from 'fp-ts/Eq';
import { fromIOK as fromIOK_ } from 'fp-ts/FromIO';
import { fromTaskK as fromTaskK_ } from 'fp-ts/FromTask';
import { constant, flow, identity, pipe } from 'fp-ts/function';
import { bindTo as bindTo_, flap as flap_ } from 'fp-ts/Functor';
import * as O from 'fp-ts/Option';
import { combineLatest, defer, EMPTY, EmptyError, from, lastValueFrom, merge, of as rxOf, throwError } from 'rxjs';
import { catchError, distinctUntilChanged as rxDistinctUntilChanged, first, map as rxMap, mergeMap, skip, skipLast, skipWhile, startWith, toArray } from 'rxjs/operators';
// -------------------------------------------------------------------------------------
// natural transformations
// -------------------------------------------------------------------------------------
/**
 * @category constructors
 */
export var fromOption = function (o) { return (O.isNone(o) ? EMPTY : of(o.value)); };
/**
 * @category constructors
 */
export var fromEither = flow(O.fromEither, fromOption);
/**
 * @category constructors
 */
export var fromIO = function (ma) { return defer(function () { return rxOf(ma()); }); };
/**
 * @category constructors
 */
export var fromReadonlyArray = function (as) { return from(as); };
/**
 * @category constructors
 */
export var fromTask = defer;
/**
 * @category constructors
 */
export var fromTaskOption = flow(fromTask, mergeMap(fromOption));
/**
 * @category constructors
 */
export var fromPredicate = function (predicate) {
    return function (a) {
        return predicate(a) ? of(a) : EMPTY;
    };
};
// -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------
var _map = function (fa, f) { return pipe(fa, map(f)); };
var _ap = function (fab, fa) { return pipe(fab, ap(fa)); };
/* istanbul ignore next */
var _alt = function (me, that) { return pipe(me, alt(that)); };
/* istanbul ignore next */
var _filter = function (fa, p) { return pipe(fa, filter(p)); };
/* istanbul ignore next */
var _filterMap = function (fa, f) { return pipe(fa, filterMap(f)); };
/* istanbul ignore next */
var _partition = function (fa, p) { return pipe(fa, partition(p)); };
/* istanbul ignore next */
var _partitionMap = function (fa, f) { return pipe(fa, partitionMap(f)); };
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
/**
 * @category combinators
 */
export var fromOptionK = function (f) {
    return flow(f, fromOption);
};
/**
 * @category combinators
 */
export var fromTaskOptionK = function (f) { return flow(f, fromTaskOption); };
// -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------
/**
 * `map` can be used to turn functions `(a: A) => B` into functions `(fa: F<A>) => F<B>` whose argument and return types
 * use the type constructor `F` to represent some computational context.
 */
export var map = function (f) { return function (fa) { return fa.pipe(rxMap(f)); }; };
/**
 * Apply a function to an argument under a type constructor.
 */
export var ap = function (fa) { return function (fab) {
    return combineLatest([fab, fa]).pipe(rxMap(function (_a) {
        var f = _a[0], a = _a[1];
        return f(a);
    }));
}; };
/**
 * @category Applicative
 */
// eslint-disable-next-line deprecation/deprecation
export var of = rxOf;
/**
 * Identifies an associative operation on a type constructor. It is similar to `Semigroup`, except that it applies to
 * types of kind `* -> *`.
 *
 * @category Alt
 */
export var alt = function (second) { return function (first) {
    return merge(first, second());
}; };
/**
 * @category Filterable
 */
export var filterMap = function (f) {
    return function (fa) {
        return fa.pipe(mergeMap(function (a) {
            return pipe(f(a), O.fold(function () { return EMPTY; }, of));
        }));
    };
};
/**
 * @category Compactable
 */
export var compact = 
/*#__PURE__*/
filterMap(identity);
/**
 * @category Filterable
 */
export var partitionMap = function (f) { return function (fa) { return ({
    left: pipe(fa, filterMap(function (a) { return O.fromEither(E.swap(f(a))); })),
    right: pipe(fa, filterMap(function (a) { return O.fromEither(f(a)); }))
}); }; };
/**
 * @category Compactable
 */
export var separate = 
/*#__PURE__*/
partitionMap(identity);
/**
 * @category Filterable
 */
export var filter = function (p) {
    return function (fa) {
        return pipe(fa, filterMap(O.fromPredicate(p)));
    };
};
/**
 * @category Filterable
 */
export var partition = function (p) {
    return function (fa) {
        return pipe(fa, partitionMap(E.fromPredicate(p, identity)));
    };
};
/**
 * @category Zero
 */
export var zero = function () { return EMPTY; };
export var dropLeft = function (n) { return skip(n); };
export var dropRight = function (n) { return skipLast(n); };
export function dropLeftWhile(predicate) {
    return skipWhile(predicate);
}
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
/**
 * @category instances
 */
export var URI = 'Observable';
/**
 * @category instances
 */
export var Functor = {
    URI: URI,
    map: _map
};
export var flap = 
/*#__PURE__*/
flap_(Functor);
/**
 * @category instances
 */
export var Pointed = {
    URI: URI,
    of: of
};
/**
 * @category instances
 */
export var Apply = {
    URI: URI,
    map: _map,
    ap: _ap
};
/**
 * Combine two effectful actions, keeping only the result of the first.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 */
export var apFirst = 
/*#__PURE__*/
apFirst_(Apply);
/**
 * Combine two effectful actions, keeping only the result of the second.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 */
export var apSecond = 
/*#__PURE__*/
apSecond_(Apply);
/**
 * @category instances
 */
export var Applicative = {
    URI: URI,
    map: _map,
    ap: _ap,
    of: of
};
/**
 * @category instances
 */
export var Alt = {
    URI: URI,
    map: _map,
    alt: _alt
};
/**
 * @category instances
 */
export var Alternative = {
    URI: URI,
    map: _map,
    ap: _ap,
    of: of,
    alt: _alt,
    zero: zero
};
/**
 * @category instances
 */
export var Compactable = {
    URI: URI,
    compact: compact,
    separate: separate
};
/**
 * @category instances
 */
export var Filterable = {
    URI: URI,
    compact: compact,
    separate: separate,
    map: _map,
    filter: _filter,
    filterMap: _filterMap,
    partition: _partition,
    partitionMap: _partitionMap
};
export var FromIO = {
    URI: URI,
    fromIO: fromIO
};
/**
 * @category combinators
 */
export var fromIOK = 
/*#__PURE__*/
fromIOK_(FromIO);
/**
 * @category instances
 */
export var FromTask = {
    URI: URI,
    fromIO: fromIO,
    fromTask: fromTask
};
/**
 * @category combinators
 */
export var fromTaskK = 
/*#__PURE__*/
fromTaskK_(FromTask);
export var fromObservable = identity;
export var FromObservable = {
    URI: URI,
    fromIO: fromIO,
    fromTask: fromTask,
    fromObservable: fromObservable
};
// -------------------------------------------------------------------------------------
// do notation
// -------------------------------------------------------------------------------------
export var Do = 
/*#__PURE__*/
of({});
export var bindTo = /*#__PURE__*/ bindTo_(Functor);
// -------------------------------------------------------------------------------------
// pipeable sequence S
// -------------------------------------------------------------------------------------
export var apS = 
/*#__PURE__*/
apS_(Apply);
// -------------------------------------------------------------------------------------
// sequence T
// -------------------------------------------------------------------------------------
export var ApT = 
/*#__PURE__*/
of([]);
// -------------------------------------------------------------------------------------
// utils
// -------------------------------------------------------------------------------------
export var toTask = function (o) {
    return function () {
        return lastValueFrom(o);
    };
};
export var toTaskOption = flow(map(O.some), startWith(O.none), toTask);
export function distinctUntilChanged(eq) {
    if (eq === void 0) { eq = eqStrict; }
    return rxDistinctUntilChanged(eq.equals);
}
export function head(o) {
    return pipe(o, first(), map(O.some), catchError(function (error) { return (error instanceof EmptyError ? rxOf(O.none) : /* istanbul ignore next */ throwError(constant(error))); }));
}
export var toReadonlyArray = toArray();
export { 
// Warning disabled since we do not use scheduler argument (Details: https://rxjs.dev/deprecations/scheduler-argument)
// eslint-disable-next-line deprecation/deprecation
combineLatest, 
// eslint-disable-next-line deprecation/deprecation
forkJoin, interval, Observable, 
// eslint-disable-next-line deprecation/deprecation
zip } from 'rxjs';
export { audit, auditTime, buffer, bufferCount, bufferTime, bufferToggle, bufferWhen, catchError, combineLatestAll, combineLatestWith, connect, count, debounce, debounceTime, defaultIfEmpty, delay, 
// eslint-disable-next-line deprecation/deprecation
delayWhen, elementAt, 
// eslint-disable-next-line deprecation/deprecation
endWith, 
// eslint-disable-next-line deprecation/deprecation
every, finalize, 
// eslint-disable-next-line deprecation/deprecation
find, 
// eslint-disable-next-line deprecation/deprecation
findIndex, first, ignoreElements, isEmpty, last, max, min, pairwise, raceWith, reduce, repeat, retry, sample, sampleTime, sequenceEqual, share, shareReplay, single, skip, skipLast, skipUntil, skipWhile, 
// eslint-disable-next-line deprecation/deprecation
startWith, take, takeLast, takeUntil, takeWhile, throttle, throttleTime, timeInterval, timeout, timestamp, withLatestFrom, zipAll, zipWith } from 'rxjs/operators';
